import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabaseStorage';
import { useAppStore } from '../store/useAppStore';
import { PublicAgentChat } from '../components/public/PublicAgentChat';
import type { AgentTemplate } from '../types/agent';

/**
 * Standalone agent runner at `/apps/agent/:templateId` (library / store deep links).
 */
export function StorefrontAgentPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const [searchParams] = useSearchParams();
  const globalSession = useAppStore((s) => s.session);

  const [template, setTemplate] = useState<AgentTemplate | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!templateId) {
      setLoadError('Missing template id');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.from('agent_templates').select('*').eq('id', templateId).maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setLoadError(error?.message || 'Template not found');
      } else {
        setTemplate(data as AgentTemplate);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-th-secondary">
        Loading…
      </div>
    );
  }

  if (loadError || !template || !globalSession) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6 text-center">
        <p className="text-th-secondary">{loadError || 'Sign in required'}</p>
        <Link to="/login" className="text-brand hover:underline">
          Sign in
        </Link>
      </div>
    );
  }

  const instanceHint = searchParams.get('instance') || '';

  return (
    <div className="min-h-screen">
      <div className="border-b border-subtle px-4 py-2 text-sm">
        <Link to="/library" className="text-th-secondary hover:text-th-primary">
          ← Library
        </Link>
        {instanceHint ? (
          <span className="ml-3 text-th-tertiary font-mono text-xs">instance {instanceHint.slice(0, 8)}…</span>
        ) : null}
      </div>
      <PublicAgentChat
        pageConfig={{
          id: templateId!,
          route_path: templateId!,
          page_title: template.name,
          page_description: template.public_description || undefined,
          app_id: 'web',
        }}
        template={template}
        session={globalSession}
      />
    </div>
  );
}
